import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon } from '@iq/react-components';
import { useNavigate } from 'react-router';
import classnames from 'classnames';
import Draggable from 'react-draggable';
import { Close as MinimizeIcon } from '@mui/icons-material';
import SimpleModal from '../SimpleModal';

import { useComponentId, useClientSizeRefresh } from '../../utils';
import AppContext from '../../AppContext';
import { getActiveSiteId } from '../../bundles/sites';
import { hasBeenUpdated } from '../../bundles/layouts';
import { getSyncHealth, getSyncedSources } from '../../bundles/sources';
import { getIsTenantOrSuperAdmin, getHasPermission } from '../../bundles/auth';
import { getFilteredActiveComponentId } from '../../bundles/components';
import config from '../../config';

import { AlarmPanel } from '../panels/AlarmPanel';
import { ComponentsPanel } from '../panels/ComponentsPanel';
import EventTimelinePanel from '../panels/EventTimelinePanel';
import { FilesPanel } from '../panels/FilesPanel';
import { TextPanel } from '../panels/TextPanel';
import { ViewerPanel } from '../panels/ViewerPanel';
import { VisualizationsPanel } from '../panels/VisualizationsPanel';
import { HealthPanel } from '../panels/HealthPanel';
import { ComponentAttributesPanel } from '../panels/ComponentAttributesPanel';
import { AnalyticsTablePanel } from '../panels/AnalyticsTablePanel';
import PanelSettings from './PanelSettings';
import SyncHealthNotification from '../SyncHealthNotification';
import { getPageType } from '../../bundles/application';
import { panelScopedPanels } from '../panels/panelSchema';
import TfrPanel from '../panels/TfrPanel';

const panels = {
  alarm: AlarmPanel,
  components: ComponentsPanel,
  'event-timeline': EventTimelinePanel,
  files: FilesPanel,
  text: TextPanel,
  'tfr-viewer': TfrPanel,
  ...(!config.onPremise && { viewer: ViewerPanel }),
  visualizations: VisualizationsPanel,
  health: HealthPanel,
  'component-properties': ComponentAttributesPanel,
  'analytics-table': AnalyticsTablePanel,
};

const renderComponent = ({
  format,
  isFullScreen,
  isFloatMode,
  options,
  pageIndex,
  panelId,
  site,
  type,
  componentScope = {},
  sectionHeight,
}) => {
  const properties = {
    format,
    isFullScreen,
    isFloatMode,
    componentScope,
    sectionHeight,
    ...options,
    pageIndex,
    panelId,
    site,
  };

  const PanelComponent = panels[type];

  if (!PanelComponent) return <div>Unknown component</div>;
  return <PanelComponent {...properties} />;
};

const Panel = React.memo(
  (properties) => {
    const {
      id,
      hidePanel = false,
      allowFullScreen,
      columnIndex,
      componentScope,
      panelScope,
      format,
      options = {},
      tableType,
      pageIndex,
      panelIndex,
      sectionIndex,
      sectionHeight,
      site,
      title,
      displayTitle,
      type,
      onHidePanel,
    } = properties;
    const { org, id: siteId } = site || {};
    const quickPanelTypes = new Set([
      'alarm',
      'analytics-table',
      'event-timeline',
      'files',
      'visualizations',
      ...(config.onPremise ? [] : ['viewer']),
    ]);

    const navigate = useNavigate();
    const draggableReference = useRef(null);

    const [settingsType, setSettingsType] = useState();
    const [settingsUrl, setSettingsUrl] = useState();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isFloatMode, setIsFloatMode] = useState(false);
    const [hide, setHide] = useState(hidePanel);
    const [showSyncHealth, setShowSyncHealth] = useState(false);

    const activeSiteId = useSelector(getActiveSiteId);
    const hasUnsavedLayoutChanges = useSelector((state) => hasBeenUpdated(state, activeSiteId));
    const activeComponentId = useSelector(getFilteredActiveComponentId);
    const isTenantOrSuperAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, org));
    const hasSitesWrite = useSelector((state) =>
      getHasPermission(state, 'sites/Write', { org, site: siteId })
    );
    const syncHealth = useSelector((state) => getSyncHealth(state, siteId));
    const syncedSources = useSelector(getSyncedSources);

    // Check panel visibility based on panelScope
    const isPanelVisible = useMemo(() => {
      if (panelScopedPanels.has(type) && panelScope) {
        const { selectedScope, selectedComponent } = panelScope;

        if (selectedScope === 'all') {
          return true;
        }

        if (selectedScope === 'specific') {
          return selectedComponent === activeComponentId;
        }
      }

      return true;
    }, [type, panelScope, activeComponentId]);

    const {
      setIsFullScreen: setIsFullScreenGlobal,
      isViewerFullScreen,
      isFullscreen: isFullscreenGlobal,
      isTouchDevice,
    } = useContext(AppContext);
    const generatedId = useComponentId();
    const panelId = id || generatedId;
    const refreshClientSize = useClientSizeRefresh();
    const pageType = useSelector(getPageType);

    const toggleFullScreen = (persist) => {
      setIsFullScreenGlobal(!isFullScreen, persist);
      setIsFullScreen(!isFullScreen);
      refreshClientSize();
    };

    useEffect(() => {
      if (!isFullscreenGlobal) {
        setIsFullScreen(false);
      }
    }, [isFullscreenGlobal]);

    useEffect(() => {
      if (options.allowFloat && type !== 'viewer' && isViewerFullScreen && !isFullScreen) {
        setIsFloatMode(true);
      } else {
        setIsFloatMode(false);
      }
    }, [options.allowFloat, type, isViewerFullScreen, isFullScreen]);

    useEffect(() => {
      setHide(hidePanel);
    }, [hidePanel]);

    const headerTooltip = {
      health:
        pageType === 'risk'
          ? `False alarms are hidden from the risk panel.
They can still be found in the list view of the Event Timeline.`
          : null,
    };

    const persistentHeader = type !== 'viewer' || isTouchDevice;

    const headerClasses = classnames({
      'panel-header': true,
      transient: !persistentHeader,
      persistent: persistentHeader,
      floating: type === 'viewer',
      draggable: isFloatMode,
      'has-tooltip': headerTooltip[type],
    });

    const openFullSettings = useCallback(() => {
      navigate(settingsUrl);
      setSettingsType(undefined);
    }, [setSettingsType, navigate, settingsUrl]);

    const handleCloseSettings = useCallback(() => setSettingsType(undefined), [setSettingsType]);

    const adminSettings = (sType) => {
      if (!sType) return null;

      return (
        <PanelSettings
          site={site}
          type={sType}
          pageIndex={pageIndex}
          sectionIndex={sectionIndex}
          columnIndex={columnIndex}
          panelIndex={panelIndex}
          onFullSettings={openFullSettings}
          onClose={handleCloseSettings}
          data-testid="settings-button"
        />
      );
    };

    const onSettings = () => {
      let url = `/sites/${activeSiteId}/admin/layout/pages/${pageIndex}`;
      if (sectionIndex >= 0) {
        url += `/sections/${sectionIndex}`;
      }
      if (columnIndex >= 0) {
        url += `/columns/${columnIndex}`;
      }
      if (panelIndex >= 0) {
        url += `/panels/${panelIndex}`;
      }
      if (quickPanelTypes.has(type) && !hasUnsavedLayoutChanges) {
        setSettingsType(type);
        setSettingsUrl(url);
      } else {
        navigate(url);
      }
    };

    const getSyncErrors = useCallback(() => {
      syncedSources.map((source) => ({
        source,
        errors: {
          syncErrors: syncHealth[`sync_${source.type}_events_${source.id}`],
          backfillErrors: syncHealth[`backfill${source.type}_events_${source.id}`],
        },
      }));
    }, [syncedSources, syncHealth]);

    // This is useful when needing to test syncError UI
    // const mockSyncErrorData = [
    //   {
    //     source: 'dec',
    //     syncErrors: {
    //       error: {
    //         failedRuns: 23,
    //         minDate: '2021-10-03T14:22:41',
    //         maxDate: '2021-12-03T14:22:41',
    //       },
    //     },
    //     backfillErrors: {
    //       error: {
    //         failedRuns: 55,
    //         minDate: '2021-10-03T14:22:41',
    //         maxDate: '2021-12-03T14:22:41',
    //       },
    //     },
    //   },
    //   {
    //     source: 'cake',
    //     syncErrors: {
    //       error: {
    //         failedRuns: 23,
    //         minDate: '2021-10-03T14:22:41',
    //         maxDate: '2021-12-03T14:22:41',
    //       },
    //     },
    //   },
    // ];
    // const syncErrors = mockSyncErrorData;

    const syncErrors = useMemo(
      () => (syncHealth ? getSyncErrors() : null),
      [syncHealth, getSyncErrors]
    );

    const syncHealthToggle =
      syncErrors && (type === 'files' || type === 'event-timeline' || type === 'tfr-viewer') ? (
        <div
          className="sync-health-toggler"
          onClick={() => {
            setShowSyncHealth(true);
          }}
        >
          <Icon
            size="s"
            icon="data-sync-error"
          />
          <div>Data sync error</div>
        </div>
      ) : null;

    const syncHealthModal = showSyncHealth ? (
      <SimpleModal
        title="Data Sync Failure"
        onClose={() => setShowSyncHealth(false)}
        size="s"
      >
        <SyncHealthNotification syncErrors={syncErrors} />
      </SimpleModal>
    ) : null;

    const panelTitle =
      displayTitle && title ? (
        <div className="panel-title">{title[0].toUpperCase() + title.slice(1)}</div>
      ) : null;

    const renderHeader = () => (
      <div className={headerClasses}>
        {panelTitle}
        {headerTooltip[type] && (
          <Button
            design="text"
            slim
            icon={
              <Icon
                icon="he-info"
                size="s"
              />
            }
            tooltip={headerTooltip[type]}
            onClick={(event) => event.preventDefault()}
          ></Button>
        )}
        {syncHealthToggle}
        <div className="tools">
          {((allowFullScreen && !isFloatMode) || type === 'viewer') && (
            <Button
              onClick={() => toggleFullScreen(type === 'viewer')}
              slim
              activity="secondary"
              icon={
                <Icon
                  size="xs"
                  icon={isFullScreen ? 'he-actualsize' : 'he-fullscreen'}
                />
              }
              tooltip={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
              data-testid="full-screen-toggle"
            />
          )}
          {(isTenantOrSuperAdmin || hasSitesWrite) && (
            <Button
              onClick={onSettings}
              slim
              activity="secondary"
              icon={
                <Icon
                  size="xs"
                  icon="he-settings"
                />
              }
              tooltip="Settings"
            />
          )}
          {isFloatMode && type !== 'viewer' && (
            <Button
              onClick={() => onHidePanel(id)}
              slim
              activity="secondary"
              icon={<MinimizeIcon fontSize="small" />}
              tooltip="Close"
            />
          )}
        </div>
      </div>
    );

    const panelClasses = classnames({
      'panel-component': true,
      'hide-panel': hide,
      [`${type}-component-type`]: type,
      'full-screen': isFullScreen,
      floating: isFloatMode,
    });

    const panelElement = useMemo(
      () => (
        <div
          ref={draggableReference}
          className={panelClasses}
        >
          {renderHeader()}
          {renderComponent({
            format,
            isFullScreen,
            isFloatMode,
            options: { ...options, ...(tableType ? { tableType } : {}) },
            pageIndex,
            panelId,
            site,
            type,
            componentScope,
            sectionHeight,
          })}
        </div>
      ),
      // renderHeader is intentionally excluded: it's a plain inline function
      // (not wrapped in useCallback), so it gets a new reference every render
      // and including it here would invalidate this memo on every render.
      [
        panelClasses,
        format,
        isFullScreen,
        isFloatMode,
        options,
        pageIndex,
        panelId,
        site,
        type,
        componentScope,
        sectionHeight,
        tableType,
        draggableReference,
      ]
    );

    if (!panels[type]) {
      return null;
    }

    if (!isPanelVisible) {
      return null;
    }

    return (
      <>
        {isFloatMode ? (
          <Draggable
            bounds=".draggable-container"
            handle=".panel-header"
            nodeRef={draggableReference}
          >
            {panelElement}
          </Draggable>
        ) : (
          panelElement
        )}
        {adminSettings(settingsType)}
        {syncHealthModal}
      </>
    );
  },
  (previousProperties, properties) =>
    JSON.stringify(previousProperties) === JSON.stringify(properties)
);

export default Panel;
